import React, { useCallback, useEffect, useMemo, useState } from "react";
import { RefreshCw, ChevronDown, ChevronUp } from "lucide-react";
import { api, extractApiError } from "../lib/api";
import PageHeader from "../components/layout/PageHeader";
import EmptyState from "../components/empty/EmptyState";
import StatusPill from "../components/status/StatusPill";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Alert, AlertDescription, AlertTitle } from "../components/ui/alert";
import { formatDateTime } from "../lib/format";
import { mailEventLabel, mailMessageTypeLabel, mailStatusLabel } from "../lib/mailLabels";

const ALL = "ALL";
const STATUS_OPTIONS = [
  { value: ALL, label: "Status: Alle" },
  { value: "SENT", label: "Gesendet" },
  { value: "FAILED", label: "Fehlgeschlagen" },
  { value: "DISABLED", label: "Deaktiviert" },
  { value: "QUEUED", label: "In Warteschlange" },
];
const MESSAGE_TYPE_OPTIONS = [
  { value: ALL, label: "Art: Alle" },
  { value: "super_admin_test", label: "Testmail" },
  { value: "tenant_invite", label: "Einladung" },
  { value: "public_lead_notification", label: "Lead-Benachrichtigung" },
  { value: "public_lead_confirmation", label: "Formularbestätigung" },
];

function messageStatusTone(status) {
  if (status === "FAILED") return "destructive";
  if (status === "SENT") return "success";
  if (status === "QUEUED") return "info";
  return "muted";
}

function eventTone(type) {
  if (!type) return "muted";
  if (
    type.includes("delivered") ||
    type.includes("accepted") ||
    type.includes("opened") ||
    type.includes("loaded")
  ) {
    return "success";
  }
  if (type.includes("clicked")) return "info";
  if (
    type.includes("failed") ||
    type.includes("bounced") ||
    type.includes("rejected") ||
    type.includes("suppressed")
  ) {
    return "destructive";
  }
  return "muted";
}

function buildTrackingLabel(message) {
  const labels = [];
  if (message.tracking_opens_enabled) labels.push("Öffnungen");
  if (message.tracking_clicks_enabled) labels.push("Klicks");
  return labels.length ? labels.join(", ") : "Aus";
}

function hasInteractionEvent(events) {
  return (events || []).some((event) =>
    ["email.opened", "email.loaded", "email.clicked", "email.link.clicked"].includes(
      event.event_type
    )
  );
}

function isUsageLimitReached(summary) {
  if (!summary || summary.limit === null) return false;
  if (summary.remaining === 0) return true;
  return Number(summary.usage_percent || 0) >= 100;
}

function usageTone(summary) {
  if (isUsageLimitReached(summary)) return "warning";
  if (summary?.warning_level === "warning") return "warning";
  if (summary?.warning_level === "unlimited") return "info";
  return "muted";
}

function usageLabel(summary) {
  if (isUsageLimitReached(summary)) return "Limit erreicht";
  if (summary?.warning_level === "warning") return "Warnung";
  if (summary?.warning_level === "unlimited") return "Unbegrenzt";
  return "Normal";
}

export default function EmailsPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [usageSummary, setUsageSummary] = useState(null);
  const [usageError, setUsageError] = useState("");
  const [filters, setFilters] = useState({
    q: "",
    status: ALL,
    messageType: ALL,
  });
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [eventsByMessageId, setEventsByMessageId] = useState({});
  const [eventsLoading, setEventsLoading] = useState({});
  const [eventsError, setEventsError] = useState({});
  const usageBreakdown = usageSummary?.breakdown_by_message_type || [];
  const countedTypes = usageSummary?.counted_message_types || [];
  const exemptTypes = usageSummary?.exempt_message_types || [];
  const observedTypes = usageSummary?.observed_message_types || [];

  const description = useMemo(
    () => "Read-only Übersicht über gesendete System- und CRM-E-Mails.",
    []
  );

  const hasActiveFilters = Boolean(
    filters.q.trim() || filters.status !== ALL || filters.messageType !== ALL
  );

  const loadMessages = useCallback(async ({ cursor = null, append = false, activeFilters = filters } = {}) => {
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
      setError("");
    }
    const params = { limit: 20 };
    const trimmedQuery = activeFilters.q.trim();
    if (trimmedQuery) params.q = trimmedQuery;
    if (activeFilters.status !== ALL) params.status = activeFilters.status;
    if (activeFilters.messageType !== ALL) params.message_type = activeFilters.messageType;
    if (cursor) params.cursor = cursor;
    try {
      const response = await api.get("/api/mail/messages/page", { params });
      const data = response.data;
      setRows((current) => (append ? [...current, ...data.items] : data.items));
      setNextCursor(data.next_cursor);
      setHasMore(Boolean(data.has_more));
    } catch (err) {
      setError(extractApiError(err, "Mail-Protokoll konnte nicht geladen werden."));
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [filters]);

  const loadUsage = useCallback(async () => {
    try {
      const response = await api.get("/api/mail/usage/current");
      setUsageSummary(response.data);
      setUsageError("");
    } catch (err) {
      setUsageSummary(null);
      setUsageError(extractApiError(err, "E-Mail-Verbrauch konnte nicht geladen werden."));
    }
  }, []);

  useEffect(() => {
    setExpandedId(null);
    loadMessages({ activeFilters: filters });
  }, [filters, loadMessages]);

  useEffect(() => {
    loadUsage();
  }, [loadUsage]);

  const loadEvents = async (messageId) => {
    setEventsLoading((current) => ({ ...current, [messageId]: true }));
    setEventsError((current) => ({ ...current, [messageId]: "" }));
    try {
      const response = await api.get(`/api/mail/messages/${messageId}/events`);
      setEventsByMessageId((current) => ({ ...current, [messageId]: response.data }));
    } catch (err) {
      setEventsError((current) => ({
        ...current,
        [messageId]: extractApiError(err, "Events konnten nicht geladen werden."),
      }));
    } finally {
      setEventsLoading((current) => ({ ...current, [messageId]: false }));
    }
  };

  const handleToggleEvents = async (messageId) => {
    if (expandedId === messageId) {
      setExpandedId(null);
      return;
    }
    setExpandedId(messageId);
    if (!(messageId in eventsByMessageId) && !eventsLoading[messageId]) {
      await loadEvents(messageId);
    }
  };

  const resetFilters = () => {
    setFilters({ q: "", status: ALL, messageType: ALL });
  };

  return (
    <div>
      <PageHeader
        title="E-Mail-Protokoll"
        description={description}
        actions={
          <Button
            variant="outline"
            onClick={() => {
              loadMessages();
              loadUsage();
            }}
            disabled={loading || loadingMore}
          >
            <RefreshCw className="w-4 h-4" /> {loading ? "Lädt …" : "Neu laden"}
          </Button>
        }
      />

      {usageSummary ? (
        <div
          data-testid="email-usage-card"
          className="surface rounded-xl p-4 mb-4 space-y-4"
        >
          <div className="flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <div className="text-sm font-medium">E-Mail-Verbrauch aktueller Monat</div>
              <div className="text-xs text-muted-foreground">
                Read-only Zusammenfassung des tenantbezogenen Mailverbrauchs.
              </div>
            </div>
            <StatusPill tone={usageTone(usageSummary)}>
              {usageLabel(usageSummary)}
            </StatusPill>
          </div>

          <div className="grid gap-3 md:grid-cols-4">
            <div className="rounded-lg border border-border bg-background px-3 py-3">
              <div className="text-xs text-muted-foreground">Verbrauch</div>
              <div className="text-lg font-semibold">{usageSummary.used}</div>
            </div>
            <div className="rounded-lg border border-border bg-background px-3 py-3">
              <div className="text-xs text-muted-foreground">Limit</div>
              <div className="text-lg font-semibold">
                {usageSummary.limit === null ? "Unbegrenzt" : usageSummary.limit}
              </div>
            </div>
            <div className="rounded-lg border border-border bg-background px-3 py-3">
              <div className="text-xs text-muted-foreground">Restbudget</div>
              <div className="text-lg font-semibold">
                {usageSummary.remaining === null ? "Unbegrenzt" : usageSummary.remaining}
              </div>
            </div>
            <div className="rounded-lg border border-border bg-background px-3 py-3">
              <div className="text-xs text-muted-foreground">Auslastung</div>
              <div className="text-lg font-semibold">
                {usageSummary.usage_percent === null ? "—" : `${usageSummary.usage_percent}%`}
              </div>
            </div>
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <div>
              <div className="text-xs font-medium uppercase tracking-[0.14em] text-muted-foreground">
                Breakdown nach Mailtyp
              </div>
              <div className="mt-2 space-y-2">
                {usageBreakdown.map((item) => (
                  <div
                    key={item.message_type}
                    className="flex items-center justify-between rounded-lg border border-border bg-background px-3 py-2 text-sm"
                  >
                    <span>{mailMessageTypeLabel(item.message_type)}</span>
                    <span className="font-medium">{item.count}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="grid gap-3 md:grid-cols-3">
              <div className="rounded-lg border border-border bg-background px-3 py-3">
                <div className="text-xs text-muted-foreground">Gezählt</div>
                <div className="mt-2 text-sm">{countedTypes.length}</div>
              </div>
              <div className="rounded-lg border border-border bg-background px-3 py-3">
                <div className="text-xs text-muted-foreground">Ausgenommen</div>
                <div className="mt-2 text-sm">{exemptTypes.length}</div>
              </div>
              <div className="rounded-lg border border-border bg-background px-3 py-3">
                <div className="text-xs text-muted-foreground">Beobachtet</div>
                <div className="mt-2 text-sm">{observedTypes.length}</div>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {usageError ? (
        <Alert variant="destructive" className="mb-4">
          <AlertTitle>Fehler</AlertTitle>
          <AlertDescription>{usageError}</AlertDescription>
        </Alert>
      ) : null}

      <div className="surface rounded-xl p-3 mb-4 flex flex-col lg:flex-row lg:items-center gap-3">
        <div className="flex-1">
          <Input
            data-testid="emails-search-input"
            value={filters.q}
            onChange={(e) => setFilters((current) => ({ ...current, q: e.target.value }))}
            placeholder="Empfänger oder Absender suchen …"
          />
        </div>
        <select
          data-testid="emails-filter-status"
          className="h-10 rounded-md border border-border bg-background px-3 text-sm lg:w-52"
          value={filters.status}
          onChange={(e) => setFilters((current) => ({ ...current, status: e.target.value }))}
        >
          {STATUS_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <select
          data-testid="emails-filter-message-type"
          className="h-10 rounded-md border border-border bg-background px-3 text-sm lg:w-56"
          value={filters.messageType}
          onChange={(e) => setFilters((current) => ({ ...current, messageType: e.target.value }))}
        >
          {MESSAGE_TYPE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        {hasActiveFilters ? (
          <Button
            type="button"
            variant="outline"
            data-testid="emails-filters-reset"
            onClick={resetFilters}
          >
            Filter zurücksetzen
          </Button>
        ) : null}
      </div>

      {error ? (
        <Alert variant="destructive" className="mb-4">
          <AlertTitle>Fehler</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      {loading ? (
        <div className="text-sm text-muted-foreground py-8 text-center">Wird geladen …</div>
      ) : rows.length === 0 ? (
        <EmptyState
          title={
            hasActiveFilters
              ? "Keine E-Mails für die aktuellen Filter gefunden."
              : "Noch keine E-Mails vorhanden."
          }
          description={
            hasActiveFilters
              ? "Passen Sie Suche oder Filter an."
              : "Sobald FyreCRM E-Mails versendet, erscheinen sie hier mit ihrem Verlauf."
          }
          testId="emails-empty"
        />
      ) : (
        <div className="obsidian overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-max text-sm">
              <thead className="bg-muted/40 border-b border-border">
                <tr className="text-left text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
                  <th className="px-4 py-3 font-medium">Zeitpunkt</th>
                  <th className="px-4 py-3 font-medium">Art</th>
                  <th className="px-4 py-3 font-medium">Versandstatus</th>
                  <th className="px-4 py-3 font-medium">Empfänger</th>
                  <th className="px-4 py-3 font-medium">From</th>
                  <th className="px-4 py-3 font-medium">Tracking</th>
                  <th className="px-4 py-3 font-medium">Ereignisverlauf</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {rows.map((message) => (
                  <React.Fragment key={message.id}>
                    <tr data-testid={`email-row-${message.id}`} className="align-top">
                      <td className="px-4 py-3 text-muted-foreground">{formatDateTime(message.created_at)}</td>
                      <td className="px-4 py-3 text-muted-foreground">
                        <div>{mailMessageTypeLabel(message.message_type)}</div>
                        {message.related_lead_id ? (
                          <a
                            href={`/leads/${message.related_lead_id}`}
                            className="text-xs text-primary underline-offset-4 hover:underline"
                          >
                            Lead öffnen
                          </a>
                        ) : null}
                      </td>
                      <td className="px-4 py-3">
                        <StatusPill tone={messageStatusTone(message.status)}>
                          {mailStatusLabel(message.status)}
                        </StatusPill>
                      </td>
                      <td className="px-4 py-3">{message.to_email_display || message.to_email_masked}</td>
                      <td className="px-4 py-3 text-muted-foreground">
                        <div>{message.from_name || "—"}</div>
                        <div className="text-xs">{message.from_email || "—"}</div>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{buildTrackingLabel(message)}</td>
                      <td className="px-4 py-3">
                        <Button
                          variant="ghost"
                          size="sm"
                          data-testid={`email-events-toggle-${message.id}`}
                          onClick={() => handleToggleEvents(message.id)}
                        >
                          {expandedId === message.id ? (
                            <ChevronUp className="w-4 h-4" />
                          ) : (
                            <ChevronDown className="w-4 h-4" />
                          )}
                          Ereignisverlauf
                        </Button>
                      </td>
                    </tr>
                    {expandedId === message.id ? (
                      <tr>
                        <td colSpan={7} className="px-4 py-4 bg-muted/20">
                          {eventsLoading[message.id] ? (
                            <div className="text-sm text-muted-foreground">Events werden geladen …</div>
                          ) : eventsError[message.id] ? (
                            <Alert variant="destructive">
                              <AlertTitle>Fehler</AlertTitle>
                              <AlertDescription>{eventsError[message.id]}</AlertDescription>
                            </Alert>
                          ) : (eventsByMessageId[message.id] || []).length === 0 ? (
                            <div className="text-sm text-muted-foreground">Keine Events vorhanden.</div>
                          ) : (
                            <div className="space-y-3" data-testid={`email-events-${message.id}`}>
                              <div className="space-y-1">
                                <div className="text-sm font-medium">Zustell- und Interaktionsverlauf</div>
                                <div className="text-xs text-muted-foreground space-y-1">
                                  <div>Versandereignisse sind unabhängig vom E-Mail-Tracking.</div>
                                  <div>
                                    Tracking-Ereignisse erscheinen nur bei aktivem Tracking und
                                    wenn der Mail-Provider sie erkannt hat.
                                  </div>
                                  <div>
                                    Öffnung oder Klick erkannt bedeutet nicht zwingend, dass die
                                    Nachricht gelesen wurde.
                                  </div>
                                </div>
                              </div>
                              {(message.tracking_opens_enabled || message.tracking_clicks_enabled) &&
                              !hasInteractionEvent(eventsByMessageId[message.id] || []) ? (
                                <div className="text-xs text-muted-foreground">
                                  Keine Öffnung oder kein Klick erkannt.
                                </div>
                              ) : null}
                              {(eventsByMessageId[message.id] || []).map((event) => (
                                <div
                                  key={event.id}
                                  className="rounded-lg border border-border bg-background px-4 py-3"
                                >
                                  <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                                    <div className="flex items-center gap-2">
                                      <StatusPill tone={eventTone(event.event_type)}>
                                        {mailEventLabel(event.event_type)}
                                      </StatusPill>
                                      <span className="text-xs text-muted-foreground">
                                        {formatDateTime(event.occurred_at || event.created_at)}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </td>
                      </tr>
                    ) : null}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {hasMore ? (
        <div className="mt-4 flex justify-center">
          <Button
            variant="outline"
            onClick={() => loadMessages({ cursor: nextCursor, append: true })}
            disabled={loadingMore}
          >
            {loadingMore ? "Weitere E-Mails werden geladen …" : "Mehr laden"}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
